import { useState } from "react";
import {
  Box,
  Group,
  NumberInput,
  Select,
  Stack,
  Textarea,
  TextInput,
  FileInput,
  Image,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { router } from "@inertiajs/react";
import { L2KH1, L2KText } from "../../../components/ui/Typography";
import { L2KButton, L2KRichTextEditor } from "../../../components/ui/Forms";
import AdminLayout from "../../../components/layouts/AdminLayout";

interface AdminPost {
  id: number;
  slug: string;
  title: string;
  summary: string | null;
  body_markdown: string | null;
  status: "draft" | "published";
  post_type: "blog" | "podcast" | "both";
  podcast_number: number | null;
  captivate_show_id: string | null;
  published_at: string | null;
  cover_image_url: string | null;
}

interface Props {
  post: AdminPost | null;
  post_types: Array<"blog" | "podcast" | "both">;
  statuses: Array<"draft" | "published">;
  errors?: string[];
}

export default function PostForm({ post, post_types, statuses, errors }: Props) {
  const [title, setTitle] = useState(post?.title ?? "");
  const [slug, setSlug] = useState(post?.slug ?? "");
  const [summary, setSummary] = useState(post?.summary ?? "");
  const [body, setBody] = useState(post?.body_markdown ?? "");
  const [status, setStatus] = useState<"draft" | "published">(
    post?.status ?? "draft"
  );
  const [postType, setPostType] = useState<"blog" | "podcast" | "both">(
    post?.post_type ?? "blog"
  );
  const [podcastNumber, setPodcastNumber] = useState<number | "">(
    post?.podcast_number ?? ""
  );
  const [captivateId, setCaptivateId] = useState(post?.captivate_show_id ?? "");
  const [publishedAt, setPublishedAt] = useState<Date | null>(
    post?.published_at ? new Date(post.published_at) : null
  );
  const [cover, setCover] = useState<File | null>(null);

  const showPodcastFields = postType === "podcast" || postType === "both";
  const isEdit = post != null;

  const autofillSlug = () => {
    if (!slug && title) {
      setSlug(
        title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
      );
    }
  };

  const submit = () => {
    const data = new FormData();
    data.append("post[title]", title);
    if (slug) data.append("post[slug]", slug);
    data.append("post[summary]", summary);
    data.append("post[body_markdown]", body);
    data.append("post[status]", status);
    data.append("post[post_type]", postType);
    if (podcastNumber !== "")
      data.append("post[podcast_number]", String(podcastNumber));
    if (captivateId) data.append("post[captivate_show_id]", captivateId);
    if (publishedAt) data.append("post[published_at]", publishedAt.toISOString());
    if (cover) data.append("post[cover_image]", cover);

    if (isEdit) {
      router.post(`/admin/posts/${post!.slug}`, data, {
        headers: { "X-HTTP-Method-Override": "PATCH" },
      });
    } else {
      router.post("/admin/posts", data);
    }
  };

  return (
    <AdminLayout>
      <Box p="lg">
        <L2KH1 mb="lg">{isEdit ? "Edit Post" : "New Post"}</L2KH1>

        {errors && errors.length > 0 && (
          <Box
            mb="md"
            p="md"
            style={{
              background: "rgba(255, 0, 0, 0.1)",
              border: "1px solid rgba(255, 0, 0, 0.3)",
              borderRadius: 8,
            }}
          >
            {errors.map((e) => (
              <L2KText key={e}>{e}</L2KText>
            ))}
          </Box>
        )}

        <Stack gap="md">
          <TextInput
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            onBlur={autofillSlug}
            required
          />

          <TextInput
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.currentTarget.value)}
            placeholder="auto-generated from title if left blank"
          />

          <Group grow>
            <Select
              label="Type"
              value={postType}
              onChange={(v) =>
                setPostType((v ?? "blog") as "blog" | "podcast" | "both")
              }
              data={post_types}
              allowDeselect={false}
            />
            <Select
              label="Status"
              value={status}
              onChange={(v) =>
                setStatus((v ?? "draft") as "draft" | "published")
              }
              data={statuses}
              allowDeselect={false}
            />
          </Group>

          <DateTimePicker
            label="Published at"
            value={publishedAt}
            onChange={(v) => setPublishedAt(v ? new Date(v as string) : null)}
            placeholder="auto-set to now when publishing"
            clearable
          />

          {showPodcastFields && (
            <Group grow>
              <NumberInput
                label="Podcast number"
                value={podcastNumber}
                onChange={(v) => setPodcastNumber(v === "" ? "" : Number(v))}
                min={0}
              />
              <TextInput
                label="Captivate Show ID"
                value={captivateId}
                onChange={(e) => setCaptivateId(e.currentTarget.value)}
              />
            </Group>
          )}

          <FileInput
            label="Cover image"
            value={cover}
            onChange={setCover}
            accept="image/*"
            placeholder={post?.cover_image_url ? "Replace image" : "Upload"}
          />
          {post?.cover_image_url && !cover && (
            <Image
              src={post.cover_image_url}
              alt={post.title}
              radius="md"
              style={{ maxHeight: 180, objectFit: "cover" }}
            />
          )}

          <Textarea
            label="Summary (excerpt)"
            value={summary}
            onChange={(e) => setSummary(e.currentTarget.value)}
            rows={3}
          />

          <L2KRichTextEditor
            label="Body"
            value={body}
            onChange={setBody}
            placeholder="Start writing…"
          />

          <Group justify="flex-end" mt="md">
            <a href="/admin/posts" style={{ textDecoration: "none" }}>
              <L2KButton variant="ghost">Cancel</L2KButton>
            </a>
            <L2KButton onClick={submit}>
              {isEdit ? "Update" : "Create"}
            </L2KButton>
          </Group>
        </Stack>
      </Box>
    </AdminLayout>
  );
}
